import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type Task, type Node, timeAgo } from '../lib/api'
import { PageHeader, Table, Badge } from '../components/ui'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

const STATUS_TONE: Record<string, 'green'|'blue'|'yellow'|'red'|'neutral'> = {
  ok: 'green', running: 'blue', pending: 'neutral', failed: 'red', canceled: 'neutral',
}
const STATUS_RU: Record<string, string> = {
  ok: 'готово', running: 'выполняется', pending: 'ожидание', failed: 'ошибка', canceled: 'отменена',
}

export function TasksPage() {
  const [openId, setOpenId] = useState<string | null>(null)

  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  // Собираем задачи со всех нод (простой способ — по каждой ноде отдельный запрос)
  const { data: all = [] } = useQuery({
    queryKey: ['all-tasks', nodes.map(n => n.id).join(',')],
    queryFn: async () => {
      const chunks = await Promise.all(nodes.map(n => api.get<Task[]>(`/nodes/${n.id}/tasks`).then(r => r.data)))
      return chunks.flat().sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    },
    enabled: nodes.length > 0,
    refetchInterval: 3000,
  })

  const [fNode, setFNode] = useState('')
  const [fStatus, setFStatus] = useState<'all'|'ok'|'running'|'pending'|'failed'>('all')
  const [fSearch, setFSearch] = useState('')
  const filtered = useMemo(() => {
    let list = all
    if (fNode) list = list.filter(t => t.node_id === fNode)
    if (fStatus !== 'all') list = list.filter(t => t.status === fStatus)
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(t => t.kind.toLowerCase().includes(q) || (t.error || '').toLowerCase().includes(q))
    }
    return list
  }, [all, fNode, fStatus, fSearch])

  return (
    <>
      <PageHeader
        title="Задачи"
        subtitle="История операций мастер → агенты. Нажмите на задачу, чтобы посмотреть прогресс и логи."
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Тип / ошибка…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
        <FilterSelect value={fStatus} onChange={setFStatus} options={[
          { value: 'all',     label: 'Все статусы' },
          { value: 'ok',      label: 'Готово' },
          { value: 'running', label: 'Выполняется' },
          { value: 'pending', label: 'Ожидание' },
          { value: 'failed',  label: 'Ошибка' },
        ]} />
      </FilterBar>
      <Table<Task>
        rows={filtered} empty={all.length === 0 ? 'Нет задач.' : 'Ничего не найдено.'}
        onRowClick={(t) => setOpenId(t.id)}
        cols={[
          { key: 'k', label: 'Тип', render: (t) => <span className="font-mono text-[12px] font-medium">{t.kind}</span> },
          { key: 'n', label: 'Нода', render: (t) => <span className="text-[13px] text-neutral-600">{nodeMap[t.node_id]?.name || t.node_id.slice(0, 8)}</span> },
          { key: 's', label: 'Статус', render: (t) => <Badge tone={STATUS_TONE[t.status] || 'neutral'}>{STATUS_RU[t.status] || t.status}</Badge> },
          { key: 'p', label: 'Прогресс', render: (t) => (
            <div className="flex items-center gap-2 min-w-[100px]">
              <div className="flex-1 h-1.5 bg-neutral-100 rounded overflow-hidden">
                <div className="h-full bg-neutral-900 transition-all" style={{ width: `${t.percent}%` }} />
              </div>
              <span className="text-[11px] text-neutral-500 w-8 text-right">{t.percent}%</span>
            </div>
          )},
          { key: 'e', label: 'Ошибка', render: (t) => t.error
            ? <span className="text-[12px] text-red-600 line-clamp-1">{t.error}</span>
            : <span className="text-neutral-400">—</span> },
          { key: 't', label: 'Создана', render: (t) => <span className="text-[12px] text-neutral-500">{timeAgo(t.created_at)}</span> },

        ]}
      />
      {openId && <TaskProgress taskId={openId} onClose={() => setOpenId(null)} />}
    </>
  )
}
